/**
 * Le fonds Thermomix — ce qui le désigne, à un seul endroit (US-97).
 *
 * Deux cents recettes rédigées pour l'application, partagées, sans
 * propriétaire, entrées en base le 30 août 2026 (`claude/fonds-mijote.md`).
 * Elles n'ont demandé **aucune colonne** : leur appartenance au fonds *est*
 * leur source, et `source_name` la porte.
 *
 * ### Pourquoi une constante partagée plutôt qu'une chaîne écrite trois fois
 *
 * Trois codes la lisent, et aucun ne peut se tromper sans conséquence :
 * l'importeur (`scripts/importer-fonds.ts`), pour qui elle est la moitié de la
 * clé d'idempotence — le couple *(source_name, titre)* ; le serveur, dont le
 * périmètre de bibliothèque en dépend (`access/perimetreDuFonds.ts`) ; et la
 * carte, qui écrit « Thermomix » près du titre. Une valeur recopiée qui
 * diverge d'une lettre ferait, selon l'endroit, deux cents doublons à
 * l'import, un fonds qui ne se sépare plus de la bibliothèque, ou un libellé
 * qui disparaît sans rien casser.
 *
 * ### « Thermomix » est une propriété du fonds, pas de la recette
 *
 * Décision du 30 août 2026. Une colonne `appareil` sur `recipe` serait plus
 * juste et plus durable ; elle coûte une migration et un champ de plus dans un
 * formulaire que la vague I cherche à alléger. **Condition de réouverture,
 * écrite pour ne pas se reposer de mémoire : le jour où une recette Thermomix
 * est saisie hors du fonds.** Ce jour-là le libellé ment, et la colonne devient
 * la bonne réponse.
 */

/**
 * La valeur de `source_name` qui fait qu'une recette appartient au fonds.
 *
 * **Renommée de « Fonds Mijoté » par la migration `029`**, et l'ordre des deux
 * gestes n'est pas indifférent : la migration réécrit les deux cents lignes
 * **avant** que cette constante change, dans le même commit. L'inverse ferait
 * créer deux cents doublons au prochain import — l'importeur ne reconnaîtrait
 * plus rien.
 */
export const SOURCE_FONDS = 'Fonds Thermomix';

/** Le nom de l'écran et du lien qui y mène. */
export const NOM_DU_FONDS = 'Le fonds Thermomix';

/**
 * Ce que la carte dit, en toutes lettres. Pas un glyphe : un pictogramme
 * monochrome dit « ça vient d'ailleurs », il ne dit pas « il te faut une
 * machine ».
 */
export const APPAREIL_DU_FONDS = 'Thermomix';

/** Cette recette vient-elle du fonds ? Seule `source_name` en décide. */
export function estDuFonds(recette: { source_name: string | null }): boolean {
  return recette.source_name === SOURCE_FONDS;
}

/**
 * Le périmètre d'une liste de recettes (US-97).
 *
 * - `livre` — les recettes de la maison et des membres, **sans** le fonds.
 *   C'est le défaut, et c'est le même raisonnement que `visibility = 'private'`
 *   : un appelant qui ne demande rien obtient le périmètre le plus restreint,
 *   et un oubli ne noie personne.
 * - `fonds` — le fonds seul, ce que l'écran dédié affiche.
 * - `tout` — les deux, ce dont la recherche a besoin pour dire « et 3 dans le
 *   fonds », et ce que l'écran des archives demande (une recette du fonds
 *   archivée doit rester retrouvable là où l'on va la rechercher).
 *
 * Le périmètre **ne remplace jamais la visibilité** : il s'ajoute à elle.
 * `tout` veut dire « le livre et le fonds », jamais « le livre de tout le
 * monde » — et c'est le seul endroit où cette story pouvait ouvrir une fuite,
 * d'où son passage au harnais de non-fuite.
 */
export const PERIMETRES = ['livre', 'fonds', 'tout'] as const;
export type Perimetre = (typeof PERIMETRES)[number];

export function isPerimetre(value: unknown): value is Perimetre {
  return typeof value === 'string' && (PERIMETRES as readonly string[]).includes(value);
}
